import React, { useState } from 'react';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CalendarColorPicker from '../CalendarColorPicker';
import { ROOM_CALENDAR_COLOR_OPTIONS } from '../../utils/calendarColors';
import {
  expandHostCalendarScopeSelection,
  hostCalendarScopeAll,
  hostCalendarScopeFromSelection,
  hostCalendarScopeRoom,
  hostCalendarScopeSelectsRoom,
  hostCalendarScopeSelectsTutor,
  hostCalendarScopeTutor,
  type HostCalendarScope,
} from '../../utils/hostCalendarScope';
import { TUTOR_IDENTITY_COLORS } from '../../utils/tutorCalendarEvents';

const TUTOR_COLOR_OPTIONS = TUTOR_IDENTITY_COLORS.map((color, index) => ({
  key: `tutor${index + 1}`,
  color,
}));

function withCurrentColor(
  options: readonly { key?: string; color: string }[],
  current: string,
): { key?: string; color: string }[] {
  if (options.some((option) => option.color.toLowerCase() === current.toLowerCase())) {
    return [...options];
  }
  return [{ color: current }, ...options];
}

export interface HostCalendarScopeChipData {
  id: string;
  label: string;
  color: string;
  /** Events in the days currently on screen. Zero hides the badge. */
  count: number;
  semanticsLabel: string;
}

export interface HostCalendarScopeBarProps {
  allLabel: string;
  allSemanticsLabel: string;
  allCount: number;
  rooms: HostCalendarScopeChipData[];
  tutors: HostCalendarScopeChipData[];
  selected: HostCalendarScope;
  onSelected: (scope: HostCalendarScope) => void;
  onRoomColorChange?: (roomId: string, color: string) => void;
  onTutorColorChange?: (tutorId: string, color: string) => void;
  chooserTitle: string;
  chooserSubtitle: string;
  roomsLabel: string;
  tutorsLabel: string;
  doneLabel: string;
}

/**
 * "All" plus the rooms and tutors, wrapping onto extra rows when they do not fit.
 * One click focuses that calendar. Clicking it again opens checkboxes for a subset.
 * Counts are events in the visible week or day.
 */
const HostCalendarScopeBar: React.FC<HostCalendarScopeBarProps> = ({
  allLabel,
  allSemanticsLabel,
  allCount,
  rooms,
  tutors,
  selected,
  onSelected,
  onRoomColorChange,
  onTutorColorChange,
  chooserTitle,
  chooserSubtitle,
  roomsLabel,
  tutorsLabel,
  doneLabel,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const roomIds = rooms.map((room) => room.id);
  const tutorIds = tutors.map((tutor) => tutor.id);
  const expanded = expandHostCalendarScopeSelection(selected, roomIds, tutorIds);
  const allSelected =
    rooms.every((room) => expanded.roomIds.has(room.id)) &&
    tutors.every((tutor) => expanded.tutorIds.has(tutor.id));
  const noneSelected = expanded.roomIds.size === 0 && expanded.tutorIds.size === 0;

  const publish = (nextRoomIds: Set<string>, nextTutorIds: Set<string>) => {
    onSelected(
      hostCalendarScopeFromSelection({
        roomIds: nextRoomIds,
        tutorIds: nextTutorIds,
        allRoomIds: roomIds,
        allTutorIds: tutorIds,
      }),
    );
  };

  const toggleRoom = (roomId: string, checked: boolean) => {
    const next = new Set(expanded.roomIds);
    if (checked) next.add(roomId);
    else next.delete(roomId);
    publish(next, new Set(expanded.tutorIds));
  };

  const toggleTutor = (tutorId: string, checked: boolean) => {
    const next = new Set(expanded.tutorIds);
    if (checked) next.add(tutorId);
    else next.delete(tutorId);
    publish(new Set(expanded.roomIds), next);
  };

  const toggleAll = () => {
    if (allSelected) {
      publish(new Set(), new Set());
      return;
    }
    publish(new Set(roomIds), new Set(tutorIds));
  };

  const openChooser = (anchor: HTMLElement) => {
    setAnchorEl(anchor);
  };

  const focusChip = (alreadySelected: boolean, next: HostCalendarScope, anchor: HTMLElement) => {
    if (alreadySelected) {
      openChooser(anchor);
      return;
    }
    onSelected(next);
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ minWidth: 0, width: '100%' }}
      >
        <ScopeChip
          label={allLabel}
          semanticsLabel={allSemanticsLabel}
          count={allCount}
          selected={selected.kind === 'all'}
          icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={(anchor) =>
            focusChip(selected.kind === 'all', hostCalendarScopeAll(), anchor)
          }
        />
        {rooms.length > 0 || tutors.length > 0 ? (
          <Divider orientation="vertical" flexItem sx={{ alignSelf: 'center', height: 20, my: 'auto' }} />
        ) : null}
        {rooms.map((room) => (
          <ScopeChip
            key={room.id}
            label={room.label}
            semanticsLabel={room.semanticsLabel}
            count={room.count}
            selected={hostCalendarScopeSelectsRoom(selected, room.id)}
            accent={room.color}
            icon={<MeetingRoomOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={(anchor) =>
              focusChip(
                hostCalendarScopeSelectsRoom(selected, room.id),
                hostCalendarScopeRoom(room.id),
                anchor,
              )
            }
            colorPicker={
              onRoomColorChange ? (
                <CalendarColorPicker
                  color={room.color}
                  options={withCurrentColor(ROOM_CALENDAR_COLOR_OPTIONS, room.color)}
                  onChange={(next) => onRoomColorChange(room.id, next)}
                  size={12}
                  ariaLabel={t('spaceCalendar.changeRoomColor', 'Change room color')}
                />
              ) : undefined
            }
          />
        ))}
        {tutors.map((tutor) => (
          <ScopeChip
            key={tutor.id}
            label={tutor.label}
            semanticsLabel={tutor.semanticsLabel}
            count={tutor.count}
            selected={hostCalendarScopeSelectsTutor(selected, tutor.id)}
            accent={tutor.color}
            icon={<SchoolOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={(anchor) =>
              focusChip(
                hostCalendarScopeSelectsTutor(selected, tutor.id),
                hostCalendarScopeTutor(tutor.id),
                anchor,
              )
            }
            colorPicker={
              onTutorColorChange ? (
                <CalendarColorPicker
                  color={tutor.color}
                  options={withCurrentColor(TUTOR_COLOR_OPTIONS, tutor.color)}
                  onChange={(next) => onTutorColorChange(tutor.id, next)}
                  size={12}
                  colorNamePrefix="spaceCalendar.tutorColorNames"
                  ariaLabel={t('spaceCalendar.changeTutorColor', 'Change tutor color')}
                />
              ) : undefined
            }
          />
        ))}
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'min(calc(100vw - 24px), 380px)', sm: 380 },
              maxHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
              mt: 1,
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {chooserTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {chooserSubtitle}
          </Typography>
        </Box>
        <Box sx={{ overflowY: 'auto', px: 1, pb: 1 }}>
          <ChooserRow
            label={allLabel}
            checked={allSelected}
            indeterminate={!allSelected && !noneSelected}
            icon={<CalendarMonthOutlinedIcon fontSize="small" />}
            onChange={toggleAll}
          />
          {rooms.length > 0 ? <SectionLabel label={roomsLabel} /> : null}
          {rooms.map((room) => (
            <ChooserRow
              key={room.id}
              label={room.label}
              count={room.count}
              checked={expanded.roomIds.has(room.id)}
              icon={<MeetingRoomOutlinedIcon fontSize="small" sx={{ color: room.color }} />}
              onChange={(checked) => toggleRoom(room.id, checked)}
            />
          ))}
          {tutors.length > 0 ? <SectionLabel label={tutorsLabel} /> : null}
          {tutors.map((tutor) => (
            <ChooserRow
              key={tutor.id}
              label={tutor.label}
              count={tutor.count}
              checked={expanded.tutorIds.has(tutor.id)}
              icon={<SchoolOutlinedIcon fontSize="small" sx={{ color: tutor.color }} />}
              onChange={(checked) => toggleTutor(tutor.id, checked)}
            />
          ))}
        </Box>
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <Button fullWidth variant="contained" onClick={() => setAnchorEl(null)}>
            {doneLabel}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

function SectionLabel({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', px: 1.5, pt: 1.25, pb: 0.25, fontWeight: 700 }}
    >
      {label}
    </Typography>
  );
}

function ChooserRow({
  label,
  count = 0,
  checked,
  indeterminate = false,
  icon,
  onChange,
}: {
  label: string;
  count?: number;
  checked: boolean;
  indeterminate?: boolean;
  icon: React.ReactNode;
  onChange: (checked: boolean) => void;
}) {
  return (
    <FormControlLabel
      sx={{ flex: 1, mx: 0, minWidth: 0, alignItems: 'center', display: 'flex' }}
      control={
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          onChange={(_, value) => onChange(value)}
        />
      }
      label={
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {icon}
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {label}
          </Typography>
          {count > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {count > 99 ? '99+' : count}
            </Typography>
          ) : null}
        </Stack>
      }
    />
  );
}

function ScopeChip({
  label,
  semanticsLabel,
  count,
  selected,
  icon,
  onClick,
  accent,
  colorPicker,
}: {
  label: string;
  semanticsLabel: string;
  count: number;
  selected: boolean;
  icon: React.ReactNode;
  onClick: (anchor: HTMLElement) => void;
  accent?: string;
  colorPicker?: React.ReactNode;
}) {
  const spoken = count > 0 ? `${semanticsLabel}, ${count}` : semanticsLabel;
  const isAll = !accent;

  return (
    <Box
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        height: 40,
        pl: colorPicker ? 0.75 : 0,
        borderRadius: '8px',
        border: '1.5px solid',
        borderColor: chipBorder(theme, selected, accent),
        bgcolor: chipBackground(theme, selected, accent),
        color: isAll && selected ? 'primary.contrastText' : 'text.primary',
        transition: 'background-color 0.18s ease, border-color 0.18s ease',
      })}
    >
      {colorPicker}
      <Box
        component="button"
        type="button"
        aria-pressed={selected}
        aria-label={spoken}
        onClick={(event) => onClick(event.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          height: '100%',
          px: 1.25,
          border: 0,
          bgcolor: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <Box sx={{ display: 'inline-flex', color: accent ?? 'inherit' }}>{icon}</Box>
        <Typography
          component="span"
          variant="body2"
          noWrap
          sx={{
            maxWidth: 160,
            fontWeight: selected ? 700 : 500,
            fontSize: 13,
            color: 'inherit',
          }}
        >
          {label}
        </Typography>
        {count > 0 ? (
          <Box
            component="span"
            sx={(theme) => ({
              px: 0.75,
              borderRadius: '4px',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.4,
              bgcolor: countBadgeBackground(theme, selected, accent),
            })}
          >
            {count > 99 ? '99+' : count}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function chipBorder(theme: Theme, selected: boolean, accent?: string) {
  if (accent && selected) return accent;
  if (!accent && selected) return theme.palette.primary.main;
  return theme.palette.divider;
}

function chipBackground(theme: Theme, selected: boolean, accent?: string) {
  if (!accent && selected) return theme.palette.primary.main;
  if (accent && selected) return alpha(accent, 0.18);
  return alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.06 : 0.72);
}

function countBadgeBackground(theme: Theme, selected: boolean, accent?: string) {
  if (!accent && selected) return alpha(theme.palette.primary.contrastText, 0.2);
  if (accent) return alpha(accent, 0.22);
  return alpha(theme.palette.common.white, 0.12);
}

export default HostCalendarScopeBar;
